import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams, useLocation, useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import Loading from "react-loading";
import Swal from "sweetalert2";
import AppSidebar from "../../components/AppSidebar";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "../../components/ui/sidebar";
import { Separator } from "../../components/ui/separator";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "../../components/ui/breadcrumb";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import SessionUpdateBtn from "../../components/SessionUpdateBtn";
import PaymentForm from "./Form";
import axios from "../../common/axios";
import { approvalMenuList } from "../../lib/menuList";
import {
  initiateBidForm,
  finalizeBid1Form,
  finalizeBid2Form,
  contractRequestSMForm,
  contractRequestForm,
  contractForm,
  changeOrderForm,
  sapPaymentForm,
  advancePaymentForm,
  vendorsEvaluationForm,
  requestChangeOrderForm,
} from "../../lib/approvalFormList";

import "../detailForm.css";

const cleanSummaryHtml = (html) => {
  const doc = new DOMParser().parseFromString(html, "text/html");
  doc
    .querySelectorAll(".LayoutTable__sub-row, .LayoutTable__footer-row")
    .forEach((el) => el.remove());
  doc.querySelectorAll("a").forEach((el) => {
    if (el.textContent.includes("바로가기")) (el.closest("td") ?? el).remove();
  });
  return doc.body.innerHTML;
};

const LoaDetailForm = ({ title }) => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [bpName, setBpName] = useState(null);
  const [projectCode, setProjectCode] = useState(null);
  const [recordNo, setRecordNo] = useState(null);
  const tab = params.get("tab");

  const getListPath = () => {
    const matched = approvalMenuList.find((item) => item.bpName === bpName);
    const basePath = matched ? matched.path : pathname.replace("/detail", "");
    return tab ? `${basePath}?tab=${tab}` : basePath;
  };
  const [errSnackBarOpen, setErrSnackBarOpen] = useState(false);
  const [errSnackBarMsg, setErrSnackBarMsg] = useState("");
  const [loaDatas, setLoaDatas] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    const paramsBpName = params.get("bpName");
    const paramsprojectCode = params.get("project_code");
    const paramsrecordNo = params.get("record_no");

    if (paramsBpName === null || !paramsrecordNo) {
      alert("잘못된 접근입니다.");
      const matched = approvalMenuList.find(
        (item) => item.bpName === paramsBpName,
      );
      const basePath = matched ? matched.path : pathname.replace("/detail", "");
      navigate(tab ? `${basePath}?tab=${tab}` : basePath);
      return;
    }

    setBpName(paramsBpName);
    setProjectCode(paramsprojectCode);
    setRecordNo(paramsrecordNo);
    return () => {
      setBpName(null);
      setProjectCode(null);
      setRecordNo(null);
    };
  }, [params, pathname, navigate, tab]);

  const fetchLoaDetail = useCallback(
    async (signal) => {
      setIsLoading(true);
      try {
        if (!bpName || !recordNo) {
          setLoaDatas(null);
          return;
        }

        const requestLoaDetail = await axios.get(
          `/loa/list/detail?bpName=${bpName}&recordNo=${recordNo}&projectCode=${projectCode}`,
          signal ? { signal } : {},
        );

        const loaDetail = requestLoaDetail.data.data;

        setLoaDatas(loaDetail);
        setLoadError("");
      } catch (err) {
        if (err.code === "ERR_CANCELED") return;

        let errorMessage =
          err.response?.data?.message || "데이터 호출에 실패하였습니다.";

        if (err.response?.status === 401) {
          errorMessage = "권한이 없습니다.";
        }

        setLoaDatas(null);
        setLoadError(errorMessage);
        setErrSnackBarOpen(true);
        setErrSnackBarMsg(errorMessage);
      } finally {
        setIsLoading(false);
      }
    },
    [bpName, projectCode, recordNo],
  );

  useEffect(() => {
    const controller = new AbortController();

    fetchLoaDetail(controller.signal);

    return () => {
      controller.abort();
    };
  }, [fetchLoaDetail]);

  const handleFormType = (bp, loaDataSet) => {
    if (bp === "Initiate Bid Approval") {
      const form = [...initiateBidForm];

      const { vendorListCount = 0 } = loaDataSet ?? {};

      const vendorListForm = Array.from({ length: vendorListCount }).map(
        (_, i) => {
          return [
            {
              type: "divider",
              title: loaDataSet[`uveVendorNameTB50${i}`],
              field: "",
            },
            {
              type: "content",
              title: "연락자",
              field: `ReverseNM${i}`,
            },
            {
              type: "content",
              title: "입찰자 이메일",
              field: `ReverseBidderEmail${i}`,
            },
            {
              type: "content",
              title: "업무용 연락처",
              field: `uuu_user_workphone${i}`,
            },
          ];
        },
      );

      if (vendorListForm.length > 0) {
        const vendorListFlat = vendorListForm.flat(1);
        const findTargetIndex = form.findIndex(
          (c) => c.title === "입찰 대상 협력사",
        );

        if (findTargetIndex >= 0) {
          form.splice(findTargetIndex + 1, 0, ...vendorListFlat);
        } else {
          form.push(...vendorListFlat);
        }
      }

      return form;
    }

    if (bp === "Finalize Bid Approval") {
      let form = [...finalizeBid1Form];

      if (loaDataSet?.BidResultCategory === "폐찰") {
        form = [...finalizeBid2Form];
      }

      const { ResultVendorListCount = 0 } = loaDataSet ?? {};

      const vendorListForm = Array.from({ length: ResultVendorListCount }).map(
        (_, i) => {
          return [
            {
              type: "divider",
              title: loaDataSet[`companyName${i}`],
              field: "",
            },
            {
              type: "content",
              title: "낙찰유무",
              field: `bidResultCategory${i}`,
            },
            {
              type: "content",
              title: "예상액",
              field: `shiAmount${i}`,
              unit: "원",
            },
            {
              type: "content",
              title: "입찰액",
              field: `bidAmount${i}`,
              unit: "원",
            },
            {
              type: "content",
              title: "입찰액/내정액",
              field: `divAmount${i}`,
              unit: "%",
            },
          ];
        },
      );

      if (vendorListForm.length > 0) {
        const vendorListFlat = vendorListForm.flat(1);

        form.push(...vendorListFlat);
      }

      return form;
    }

    if (bp === "Contract Request Approval") {
      const { vendorListCount = 0, SMYesNoPD } = loaDataSet ?? {};

      let form = [];

      if (SMYesNoPD === "예") {
        form = [...contractRequestSMForm];
      } else {
        form = [...contractRequestForm];
      }

      const vendorListForm = Array.from({ length: vendorListCount }).map(
        (_, i) => {
          return {
            type: "content",
            title: "협력사명",
            field: `vendorList${i}`,
          };
        },
      );

      if (vendorListForm.length > 0) {
        const vendorListFlat = vendorListForm.flat(1);

        form.push(...vendorListFlat);
      }

      return form;
    }

    if (bp === "SHI Contract Approval") {
      return contractForm;
    }

    if (bp === "SHI CO Req Approval") {
      return requestChangeOrderForm;
    }

    if (bp === "SHI CO Approval") {
      return changeOrderForm;
    }

    if (bp === "SPA Approval") {
      return sapPaymentForm;
    }

    if (bp === "Advance Payment Approval") {
      return advancePaymentForm;
    }

    if (bp === "Vendors Evaluation Approval") {
      return vendorsEvaluationForm;
    }

    return null;
  };

  const closeSnackBar = () => {
    setErrSnackBarOpen(false);
    setErrSnackBarMsg("");
  };

  useEffect(() => {
    if (!errSnackBarOpen) return;
    const timer = setTimeout(closeSnackBar, 15000);
    return () => clearTimeout(timer);
  }, [errSnackBarOpen]);

  const handleApprovalAction = (type, project_code, record_no) => {
    if (!(type === "Approved" || type === "Rejected")) return;
    if (actionLoading) return;

    return Swal.fire({
      icon: "question",
      title: "결재를 진행하시겠습니까?",
      text: "결재 코멘트를 입력하여 주시기 바랍니다.",
      input: "text",
      showCancelButton: true,
      confirmButtonText: type === "Approved" ? "승인" : "반려",
      cancelButtonText: "취소",
      showLoaderOnConfirm: true,
      preConfirm: async (comment) => {
        try {
          setActionLoading(true);

          await axios.post("/loa/update", {
            recordNo: record_no,
            bpName,
            projectCode: project_code,
            result: type === "Approved" ? "승인" : "반려",
            comment,
          });

          return true;
        } catch (error) {
          Swal.showValidationMessage(
            error.response?.data?.message || "처리 중 오류 발생",
          );
          setActionLoading(false);
        }
      },
      allowOutsideClick: () => !Swal.isLoading(),
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire({
          icon: "success",
          title: "결재가 완료되었습니다.",
        });

        fetchLoaDetail();
      }
      setActionLoading(false);
    });
  };

  const handleCancelSubmission = async (project_code, record_no) => {
    if (actionLoading) return;

    const confirmed = await Swal.fire({
      title: "상신 취소",
      text: "상신을 취소하시겠습니까?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "확인",
      cancelButtonText: "취소",
    });
    if (!confirmed.isConfirmed) return;

    try {
      await axios.post("/loa/cancel", {
        recordNo: record_no,
        bpName,
        projectCode: project_code,
      });
      await Swal.fire({
        icon: "success",
        title: "상신 취소 완료",
        text: "상신이 정상적으로 취소되었습니다.",
        confirmButtonText: "확인",
      });
      navigate(getListPath());
    } catch (err) {
      Swal.fire(
        "오류",
        err.response?.data?.message || "처리 중 오류 발생",
        "error",
      );
    }
  };

  const handleSummaryClick = useCallback(async () => {
    setSummaryOpen(true);
    setSummaryLoading(true);
    try {
      if (!bpName || !recordNo) {
        setSummaryHtml("<p>필수 정보가 없습니다.</p>");
        return;
      }
      const r = await axios.get(
        `/loa/summary?bpName=${encodeURIComponent(bpName)}&projectCode=${encodeURIComponent(projectCode ?? "")}&recordNo=${encodeURIComponent(recordNo ?? "")}`,
      );
      setSummaryHtml(cleanSummaryHtml(r.data.html ?? ""));
    } catch (e) {
      setSummaryHtml("<p>요약을 불러오는 데 실패했습니다.</p>");
    } finally {
      setSummaryLoading(false);
    }
  }, [bpName, projectCode, recordNo]);

  const showCancelButton = loaDatas?.isCancelable === true;
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [summaryHtml, setSummaryHtml] = useState("");
  const [summaryLoading, setSummaryLoading] = useState(false);

  const unifierUrl = process.env.REACT_APP_UNIFIER_URL;

  const topbarJsx = (
    <header className="shrink-0 flex items-center gap-2 h-12 px-3 border-b border-[#e4e4e7] bg-white">
      <SidebarTrigger />
      <Separator orientation="vertical" className="h-4" />
      <Breadcrumb className="min-w-0 overflow-hidden">
        <BreadcrumbList className="flex-nowrap overflow-hidden">
          <BreadcrumbItem>LOA</BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem className="min-w-0 overflow-hidden">
            <BreadcrumbPage className="truncate">
              {approvalMenuList.find((item) => item.bpName === bpName)?.label ??
                title}
            </BreadcrumbPage>
          </BreadcrumbItem>
          {recordNo && (
            <>
              <BreadcrumbSeparator />
              <BreadcrumbItem className="min-w-0 overflow-hidden">
                <BreadcrumbPage className="truncate">{recordNo}</BreadcrumbPage>
              </BreadcrumbItem>
            </>
          )}
        </BreadcrumbList>
      </Breadcrumb>
      <div className="ml-auto flex items-center gap-2 shrink-0">
        <Button
          variant="outline"
          size="sm"
          asChild
          className="hidden md:inline-flex"
        >
          <a
            href={unifierUrl || "#"}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => {
              if (!unifierUrl) e.preventDefault();
            }}
          >
            유니파이어 바로가기
          </a>
        </Button>
        <SessionUpdateBtn />
      </div>
    </header>
  );

  if (isLoading) {
    return (
      <SidebarProvider defaultOpen>
        <AppSidebar bpName={bpName} />
        <SidebarInset className="h-svh flex flex-col overflow-hidden">
          {topbarJsx}
          <div
            style={{
              display: "flex",
              flex: 1,
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Loading color="#007bff" />
          </div>
        </SidebarInset>
      </SidebarProvider>
    );
  }

  if (loadError || !loaDatas) {
    return (
      <SidebarProvider defaultOpen>
        <AppSidebar bpName={bpName} />
        <SidebarInset className="h-svh flex flex-col overflow-hidden">
          {topbarJsx}
          <div className="flex-1 min-h-0 overflow-y-auto">
            <div className="total-form-body">
              <div className="detail-form-body">
                <div className="detail-form-title-body">
                  <div className="detail-form-title" />
                  <Button
                    variant="outline"
                    size="sm"
                    className="detail-top-nav-button"
                    style={{ marginLeft: "auto" }}
                    onClick={() => navigate(getListPath())}
                  >
                    목록
                  </Button>
                </div>
                <div
                  style={{ textAlign: "center", padding: 40, color: "#667085" }}
                >
                  {loadError || "데이터를 불러올 수 없습니다."}
                </div>
              </div>
            </div>
          </div>
        </SidebarInset>
        {errSnackBarOpen && (
          <div className="fixed bottom-4 right-4 z-50 flex items-center gap-2 rounded-md bg-[#323232] px-4 py-3 text-sm text-white shadow-lg">
            <span>{errSnackBarMsg}</span>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 text-white hover:bg-white/10 hover:text-white"
              aria-label="close"
              onClick={closeSnackBar}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        )}
      </SidebarProvider>
    );
  }

  const hasBottomButtons = showCancelButton || loaDatas?.approvalTurn;

  return (
    <SidebarProvider defaultOpen>
      <AppSidebar bpName={bpName} />
      <SidebarInset className="h-svh flex flex-col overflow-hidden">
        {topbarJsx}
        <div className="flex-1 min-h-0 overflow-y-auto">
          <div className="total-form-body">
            <div className="detail-form-body">
              <div className="detail-form-title-body">
                <div className="detail-form-title">
                  {loaDatas?.loaDataSet?.subjectIn}
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginLeft: "auto",
                  }}
                >
                  {loaDatas?.loaDataSet?.original_record_no && (
                    <span
                      style={{
                        fontSize: 13,
                        color: "#667085",
                        whiteSpace: "nowrap",
                      }}
                    >
                      레코드 번호:{" "}
                      <strong>{loaDatas.loaDataSet.original_record_no}</strong>
                    </span>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    className="detail-top-nav-button"
                    onClick={() => navigate(getListPath())}
                  >
                    목록
                  </Button>
                </div>
              </div>
              <PaymentForm
                formColumns={handleFormType(bpName, loaDatas?.loaDataSet)}
                approvalLine={loaDatas?.approvalLine ?? []}
                datas={loaDatas?.loaDataSet ?? {}}
                recordNo={recordNo}
                onSummaryClick={handleSummaryClick}
              />
            </div>
            {hasBottomButtons && (
              <div className="form-button-box">
                {showCancelButton && (
                  <Button
                    variant="outline"
                    className="form-button border-amber-500 text-amber-600 hover:bg-amber-50 hover:text-amber-700"
                    disabled={actionLoading}
                    onClick={() =>
                      handleCancelSubmission(projectCode, recordNo)
                    }
                  >
                    상신 취소
                  </Button>
                )}

                {loaDatas?.approvalTurn?.myTurn && (
                  <>
                    <Button
                      variant="default"
                      className="form-button"
                      disabled={actionLoading}
                      onClick={() => {
                        handleApprovalAction("Approved", projectCode, recordNo);
                      }}
                    >
                      {actionLoading ? "처리중..." : "승인"}
                    </Button>

                    <Button
                      variant="outline"
                      className="form-button"
                      disabled={actionLoading}
                      onClick={() => {
                        handleApprovalAction("Rejected", projectCode, recordNo);
                      }}
                    >
                      {actionLoading ? "처리중..." : "반려"}
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>
          {errSnackBarOpen && (
            <div className="fixed bottom-4 right-4 z-50 flex items-center gap-2 rounded-md bg-[#323232] px-4 py-3 text-sm text-white shadow-lg">
              <span>{errSnackBarMsg}</span>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 text-white hover:bg-white/10 hover:text-white"
                aria-label="close"
                onClick={closeSnackBar}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          )}

          <Dialog open={summaryOpen} onOpenChange={setSummaryOpen}>
            <DialogContent className="max-w-5xl w-full p-0 max-h-[85vh] flex flex-col">
              <DialogHeader className="px-6 py-4 border-b border-[#eaecf0] shrink-0">
                <DialogTitle>품의 요약</DialogTitle>
              </DialogHeader>
              <div className="overflow-y-auto">
                {summaryLoading ? (
                  <div style={{ textAlign: "center", padding: 40 }}>
                    불러오는 중...
                  </div>
                ) : (
                  <div dangerouslySetInnerHTML={{ __html: summaryHtml }} />
                )}
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
};

export default LoaDetailForm;
